import { useState, useEffect, useRef, useMemo, memo, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchActiveBanners, Banner } from "@/services/bannerService";
import { getOptimizedImageUrl } from "@/utils/imageUtils";

import homeBannerSeed from '@/generated/home-banners.json';
import homeMedia from '@/generated/home-media.json';
import { getBannerPlayback } from '@/lib/heroMedia';

const mediaVariants = homeMedia as Record<string, { mobile: string; desktop: string; poster: string; mobileWebm?: string }>;

export interface ExtendedBanner extends Banner {
  isDefault?: boolean;
}

export const DEFAULT_BANNERS: ExtendedBanner[] = [
  {
    bannerId: "default-raw-pyrite",
    placement: "home",
    mediaType: "image",
    mediaUrl: "/banners/raw-pyrite-banner.webp",
    mobileMediaUrl: "/banners/raw-pyrite-banner-mobile.webp",
    buttonText: "",
    redirectUrl: "",
    displayOrder: 0,
    isActive: true,
    isDefault: true,
  },
  {
    bannerId: "default-moonstone",
    placement: "home",
    mediaType: "image",
    mediaUrl: "/banners/moonstone-banner.webp",
    mobileMediaUrl: "/banners/moonstone-banner-mobile.webp",
    buttonText: "",
    redirectUrl: "",
    displayOrder: 1,
    isActive: true,
    isDefault: true,
  },
  {
    bannerId: "default-carnelian",
    placement: "home",
    mediaType: "image",
    mediaUrl: "/banners/carnelian-banner.webp",
    mobileMediaUrl: "/banners/carnelian-banner-mobile.webp",
    buttonText: "",
    redirectUrl: "",
    displayOrder: 2,
    isActive: true,
    isDefault: true,
  },
];

const Hero = memo(() => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [modernCodec] = useState(() => typeof document !== 'undefined' && !!document.createElement('video').canPlayType('video/webm; codecs="vp9, opus"'));
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const update = () => setCompact(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [failedBannerIds, setFailedBannerIds] = useState<Set<string>>(new Set());
  const [failedOptimizedBannerIds, setFailedOptimizedBannerIds] = useState<Set<string>>(new Set());

  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const sectionRef = useRef<HTMLElement | null>(null);

  // Fetch admin-managed banners
  const { data: bannersData, isLoading } = useQuery<Banner[]>({
    queryKey: ["active-banners"],
    queryFn: fetchActiveBanners,
    // Revalidate immediately so the admin's latest selection remains authoritative.
    initialData: homeBannerSeed as Banner[],
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Filter active admin banners sorted by displayOrder
  const activeAdminBanners = useMemo(() => {
    const raw = Array.isArray(bannersData) ? bannersData : [];
    return [...raw]
      .filter((b) => b && b.isActive && b.placement !== 'category' && !failedBannerIds.has(b.bannerId))
      .sort(
        (a, b) =>
          (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.displayOrder ?? Number.MAX_SAFE_INTEGER)
      );
  }, [bannersData, failedBannerIds]);

  // Determine visible banners: use active admin banners if present, otherwise default fallback banners
  const visibleBanners: ExtendedBanner[] = useMemo(() => {
    const list = activeAdminBanners.length > 0 ? activeAdminBanners : DEFAULT_BANNERS;
    return list.filter((b) => !failedBannerIds.has(b.bannerId));
  }, [activeAdminBanners, failedBannerIds]);

  // Keep activeIndex within valid bounds when visibleBanners length changes
  useEffect(() => {
    if (visibleBanners.length > 0 && activeIndex >= visibleBanners.length) {
      setActiveIndex(0);
    }
  }, [visibleBanners.length, activeIndex]);

  // Every new slide starts muted; sound is enabled only by a visitor's click.
  useEffect(() => {
    setSoundEnabled(false);
    const video = videoRefs.current[activeIndex];
    if (video) video.muted = true;
  }, [activeIndex]);

  useEffect(() => {
    if (isInView && !isTabHidden) return;
    setSoundEnabled(false);
    const video = videoRefs.current[activeIndex];
    if (video) video.muted = true;
  }, [activeIndex, isInView, isTabHidden]);

  // Check prefers-reduced-motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    const listener = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", listener);
    return () => mediaQuery.removeEventListener("change", listener);
  }, []);

  // Track document visibility to pause timer & playback when browser tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabHidden(document.hidden);
    };
    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Start playback when the banner scrolls into view, including after the loading skeleton.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [isLoading, visibleBanners.length]);

  const goToNextSlide = () => {
    if (visibleBanners.length === 0) return;
    setSoundEnabled(false);
    setActiveIndex((prev) => (prev + 1) % visibleBanners.length);
  };

  const goToPrevSlide = () => {
    if (visibleBanners.length === 0) return;
    setSoundEnabled(false);
    setActiveIndex((prev) => (prev - 1 + visibleBanners.length) % visibleBanners.length);
  };

  const toggleSound = (event: MouseEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    const video = videoRefs.current[index];
    if (!video) return;
    const enable = video.muted;
    video.muted = !enable;
    setSoundEnabled(enable);
    if (enable && video.paused && isInView && !isTabHidden) {
      void video.play().catch(() => { /* The visitor can retry with the native play gesture. */ });
    }
  };

  // Current active banner
  const currentBanner = visibleBanners[activeIndex] || visibleBanners[0];
  const isVideoSlide = currentBanner?.mediaType === "video";

  // Give visitors time to see a banner before rotating; video slides advance after playback.
  useEffect(() => {
    if (
      visibleBanners.length <= 1 ||
      isHovered ||
      isFocused ||
      isTabHidden ||
      !isInView ||
      prefersReducedMotion ||
      isVideoSlide
    ) {
      return;
    }

    const timer = setTimeout(() => {
      goToNextSlide();
    }, 8000);

    return () => clearTimeout(timer);
  }, [
    activeIndex,
    visibleBanners.length,
    isVideoSlide,
    isHovered,
    isFocused,
    isTabHidden,
    isInView,
    prefersReducedMotion,
  ]);

  // Handle video playback: play active video from start, pause inactive videos
  useEffect(() => {
    visibleBanners.forEach((banner, idx) => {
      if (banner.mediaType === "video") {
        const vid = videoRefs.current[idx];
        if (vid) {
          // An SSR source may fail before React attaches its error handlers.
          if (vid.error || vid.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) vid.load();
          if (idx === activeIndex && isInView && !isTabHidden && !prefersReducedMotion) {
            vid.currentTime = 0;
            vid.play().catch(() => {
              // Autoplay block handled safely
            });
          } else {
            vid.pause();
          }
        }
      }
    });
  }, [activeIndex, visibleBanners, isInView, isTabHidden, prefersReducedMotion, compact, failedOptimizedBannerIds]);

  const handleMediaError = (bannerId: string) => {
    setFailedBannerIds((prev) => {
      const next = new Set(prev);
      next.add(bannerId);
      return next;
    });
  };

  const handleRedirect = (url?: string | null) => {
    const trimmed = url?.trim();
    if (!trimmed) return;

    if (/^(javascript|data|vbscript):/i.test(trimmed)) {
      return;
    }

    if (/^https?:\/\//i.test(trimmed)) {
      window.open(trimmed, "_blank", "noopener,noreferrer");
    } else if (trimmed.startsWith("#")) {
      const element = document.getElementById(trimmed.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      navigate(trimmed);
    }
  };

  // Show the local, preloaded banner immediately while admin banners load.
  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-black aspect-[3/2] sm:aspect-video max-h-[760px] m-0 p-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      aria-label="Homepage Promotional Banner Carousel"
    >
      {/* Banner Slides */}
      {visibleBanners.map((banner, index) => {
        const isActive = index === activeIndex;
        if (!isActive) return null;
        const buttonText = banner.buttonText?.trim();
        const redirectUrl = banner.redirectUrl?.trim();
        const showButton = Boolean(buttonText && redirectUrl && !banner.isDefault);
        const isWholeBannerClickable = Boolean(!showButton && redirectUrl && !banner.isDefault);
        const mobileUrl = banner.mobileMediaUrl || banner.mediaUrl;
        const useOriginal = failedOptimizedBannerIds.has(banner.bannerId);
        const playback = getBannerPlayback(banner, compact, useOriginal ? {} : homeMedia, modernCodec && !failedOptimizedBannerIds.has(banner.bannerId + ':webm'));
        const nativeSources = !useOriginal && !banner.mobileMediaUrl ? mediaVariants[banner.mediaUrl] : undefined;
        const desktopImageUrl = useOriginal ? banner.mediaUrl : getOptimizedImageUrl(banner.mediaUrl, { width: 1600, quality: 75, fit: "inside" });
        const mobileImageUrl = useOriginal ? mobileUrl : getOptimizedImageUrl(mobileUrl, { width: 900, quality: 72, fit: "inside" });

        return (
          <div
            key={banner.bannerId || index}
            className={`absolute inset-0 z-10 h-full w-full ${isWholeBannerClickable ? "cursor-pointer" : ""}`}
            onClick={
              isWholeBannerClickable && isActive
                ? () => handleRedirect(redirectUrl)
                : undefined
            }
            onKeyDown={
              isWholeBannerClickable && isActive
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleRedirect(redirectUrl);
                    }
                  }
                : undefined
            }
            role={isWholeBannerClickable ? "button" : undefined}
            tabIndex={isWholeBannerClickable && isActive ? 0 : -1}
            aria-label={
              isWholeBannerClickable ? `Promotional banner redirect to ${redirectUrl}` : undefined
            }
          >
            {banner.mediaType === "video" ? (
              <video
                ref={(el) => {
                  videoRefs.current[index] = el;
                }}
                src={nativeSources ? undefined : playback.src}
                poster={playback.poster}
                muted={!soundEnabled}
                loop={visibleBanners.length === 1}
                playsInline
                preload="metadata"
                onEnded={() => {
                  if (isActive && visibleBanners.length > 1) {
                    goToNextSlide();
                  }
                }}
                className="h-full w-full object-cover object-center"
                onError={(event) => {
                  // React bubbles source errors, including skipped media queries.
                  // Only a failure of the video itself belongs to this handler.
                  if (event.target !== event.currentTarget) return;
                  const original = compact && banner.mobileMediaUrl ? banner.mobileMediaUrl : banner.mediaUrl;
                  const failed = videoRefs.current[index]?.currentSrc || playback.src;
                  if (failed.includes('.webm')) {
                    setFailedOptimizedBannerIds(previous => new Set(previous).add(banner.bannerId + ':webm'));
                  } else if (!useOriginal && playback.src !== original) {
                    setFailedOptimizedBannerIds(previous => new Set(previous).add(banner.bannerId));
                  } else handleMediaError(banner.bannerId);
                }}
              >
                {nativeSources && <>
                  {nativeSources.mobileWebm && !failedOptimizedBannerIds.has(banner.bannerId + ':webm') && <source src={nativeSources.mobileWebm} type="video/webm" media="(max-width: 639px)" />}
                  <source src={nativeSources.mobile} type="video/mp4" media="(max-width: 639px)" />
                  <source src={nativeSources.desktop} type="video/mp4" onError={() => setFailedOptimizedBannerIds(previous => new Set(previous).add(banner.bannerId))} />
                </>}
              </video>
            ) : (
              <picture className="block h-full w-full">
                <source media="(max-width: 639px)" srcSet={mobileImageUrl} />
                <img
                  src={desktopImageUrl}
                  alt={buttonText || "UMA'S AURA promotional banner"}
                  className="h-full w-full object-cover object-center"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onError={() => {
                    if (!useOriginal && (desktopImageUrl !== banner.mediaUrl || mobileImageUrl !== mobileUrl)) {
                      setFailedOptimizedBannerIds((previous) => new Set(previous).add(banner.bannerId));
                    } else {
                      handleMediaError(banner.bannerId);
                    }
                  }}
                />
              </picture>
            )}

            {banner.mediaType === "video" && (
              <button
                type="button"
                onClick={(event) => toggleSound(event, index)}
                onKeyDown={(event) => event.stopPropagation()}
                aria-label={soundEnabled ? "Mute banner video" : "Turn on banner sound"}
                aria-pressed={soundEnabled}
                className="absolute right-4 top-4 z-30 inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-full border border-white/65 bg-black/65 px-3 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:right-6 sm:top-6"
              >
                {soundEnabled ? <Volume2 className="h-5 w-5" aria-hidden="true" /> : <VolumeX className="h-5 w-5" aria-hidden="true" />}
                <span className="hidden text-xs font-semibold sm:inline">{soundEnabled ? "Sound on" : "Sound off"}</span>
              </button>
            )}

            {/* Dynamic CTA Button (Admin Banners only) */}
            {showButton && isActive && (
              <div className="absolute inset-x-0 bottom-12 z-20 flex justify-center px-4 sm:bottom-16 pointer-events-auto">
                <Button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRedirect(redirectUrl);
                  }}
                  className="min-h-11 rounded-lg border border-amber-300/70 bg-gradient-to-r from-amber-500 to-yellow-600 px-6 py-3 text-sm font-semibold tracking-wide text-black shadow-lg transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:px-8 sm:text-base cursor-pointer"
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {/* Carousel Navigation Arrows (shown only when > 1 banner) */}
      {visibleBanners.length > 1 && (
        <>
          <button
            type="button"
            onClick={goToPrevSlide}
            className="absolute left-3 top-1/2 z-30 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-amber-300/60 bg-black/55 text-white shadow-md backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 sm:h-11 sm:w-11 cursor-pointer"
            aria-label="Previous banner"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>

          <button
            type="button"
            onClick={goToNextSlide}
            className="absolute right-3 top-1/2 z-30 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-amber-300/60 bg-black/55 text-white shadow-md backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 sm:h-11 sm:w-11 cursor-pointer"
            aria-label="Next banner"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </>
      )}

      {/* Pagination Dots (shown only when > 1 banner) */}
      {visibleBanners.length > 1 && (
        <div className="absolute bottom-3 sm:bottom-4 inset-x-0 z-30 flex justify-center items-center gap-1.5 pointer-events-auto">
          {visibleBanners.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => { setSoundEnabled(false); setActiveIndex(idx); }}
              aria-label={`Go to banner ${idx + 1}`}
              aria-current={idx === activeIndex}
              // The dot stays 8px tall; the button around it is padded out to the
              // 24px minimum touch target instead of enlarging the visual.
              className="group flex h-6 min-w-[24px] items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 cursor-pointer"
            >
              <span
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === activeIndex
                    ? "w-6 sm:w-8 bg-amber-400"
                    : "w-2 bg-white/50 group-hover:bg-white/80"
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
});

Hero.displayName = "Hero";

export default Hero;
